import { useState } from 'react';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useSeo } from '@/hooks/useSeo';

const FORM_ID = 'contact-form';
const SUBMIT_ADDR = 'https://readdy.ai/api/form/db4nfa9rqll58fj4o9tg';

const contacts = [
  { icon: 'ri-mail-line', label: '邮箱', value: 'contact@91niu.example.com' },
  { icon: 'ri-phone-line', label: '电话', value: '400-000-0000（演示号码）' },
  { icon: 'ri-map-pin-line', label: '地址', value: '北京市朝阳区示例大厦 18 层（演示地址）' },
  { icon: 'ri-time-line', label: '服务时间', value: '周一至周五 09:00 - 18:00' },
];

export default function ContactPage() {
  useSeo({
    title: '联系我们 - 9·1牛网',
    description: '欢迎联系 9·1牛网，无论是内容反馈、商务合作还是使用建议，都可以通过我们的联系方式或在线留言与我们取得联系。',
    keywords: '联系我们,9·1牛网,在线留言,商务合作',
  });

  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [messageLength, setMessageLength] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get('company_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      formEl.reset();
      setMessageLength(0);
      return;
    }

    const name = String(formData.get('name') ?? '').trim();
    const email = String(formData.get('email') ?? '').trim();
    const message = String(formData.get('message') ?? '').trim();

    if (!name || !email || !message) {
      setStatus('error');
      setFormError('请填写完整的姓名、邮箱和留言内容。');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus('error');
      setFormError('请填写正确的邮箱地址。');
      return;
    }
    if (message.length > 500) {
      setStatus('error');
      setFormError('留言内容不能超过 500 个字符。');
      return;
    }

    const payload = new URLSearchParams();
    payload.append('name', name);
    payload.append('email', email);
    payload.append('message', message);

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg: string =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && code === 'OK' && !isSpam) {
        setStatus('success');
        formEl.reset();
        setMessageLength(0);
      } else {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试。');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1 w-full px-4 md:px-8 py-6 md:py-8">
        <Breadcrumb items={[{ label: '联系我们' }]} />

        <header className="mt-4 mb-8">
          <h1 className="font-heading font-black text-2xl md:text-3xl text-foreground-950">联系我们</h1>
          <p className="mt-2 text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
            如果你对内容、合作或使用体验有任何建议，欢迎通过以下方式与我们取得联系，我们期待你的反馈。
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 md:gap-8">
          <section className="lg:col-span-2 space-y-4">
            {contacts.map((c) => (
              <div key={c.label} className="flex items-start gap-4 p-4 md:p-5 rounded-lg bg-background-100 border border-background-200">
                <span className="w-11 h-11 shrink-0 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center">
                  <i className={`${c.icon} text-xl`} />
                </span>
                <div>
                  <p className="text-xs text-foreground-500">{c.label}</p>
                  <p className="mt-1 text-sm font-medium text-foreground-900">{c.value}</p>
                </div>
              </div>
            ))}
            <p className="text-xs text-foreground-500 leading-relaxed px-1">
              提示：本页联系方式与表单均为演示示例，暂未接入真实后台接口，提交结果仅用于功能演示。
            </p>
          </section>

          <section className="lg:col-span-3">
            <div className="p-5 md:p-7 rounded-lg bg-background-50 border border-background-200">
              <h2 className="font-heading font-bold text-lg text-foreground-950 mb-1">在线留言</h2>
              <p className="text-sm text-foreground-500 mb-5">请填写以下信息，我们会尽快与你联系（演示功能）</p>

              {status === 'success' && (
                <div className="mb-4 flex items-start gap-3 p-4 rounded-md bg-primary-50 border border-primary-200">
                  <i className="ri-checkbox-circle-line text-primary-600 text-lg" />
                  <div>
                    <p className="text-sm font-medium text-primary-800">提交成功（演示）</p>
                    <p className="text-xs text-foreground-600 mt-0.5">感谢你的留言，我们已收到你的反馈。</p>
                  </div>
                </div>
              )}
              {status === 'error' && formError && (
                <div className="mb-4 flex items-start gap-3 p-4 rounded-md bg-accent-50 border border-accent-200">
                  <i className="ri-error-warning-line text-accent-600 text-lg" />
                  <div>
                    <p className="text-sm font-medium text-accent-800">提交失败</p>
                    <p className="text-xs text-foreground-600 mt-0.5">{formError}</p>
                  </div>
                </div>
              )}

              <form id={FORM_ID} data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    姓名 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入你的姓名"
                    className="w-full h-11 px-4 text-sm rounded-md border border-background-300 bg-background-100 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    邮箱 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="请输入你的邮箱"
                    className="w-full h-11 px-4 text-sm rounded-md border border-background-300 bg-background-100 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    留言内容 <span className="text-accent-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    onChange={(e) => setMessageLength(e.target.value.length)}
                    placeholder="请输入你的留言内容（最多 500 字）"
                    className="w-full px-4 py-3 text-sm rounded-md border border-background-300 bg-background-100 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 resize-none"
                  />
                  <p className="mt-1 text-xs text-foreground-400 text-right">{messageLength}/500</p>
                </div>

                <input
                  type="text"
                  name="company_alt"
                  className="form-guard-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                >
                  {status === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" /> 提交中...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line" /> 提交留言
                    </>
                  )}
                </button>
              </form>
            </div>
          </section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}